import React, { useEffect, useState } from 'react';
import { Loader2, Monitor, Smartphone, Send, AlertTriangle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { newsletterApi } from '../services/api';
import { toast } from '../hooks/use-toast';

const AUD_LABEL = { members: 'registered members', contacts: 'newsletter contacts' };

export function PreviewDialog({ campaignId, audience, onClose }) {
  const [data, setData] = useState(null);
  const [mode, setMode] = useState('phone');
  useEffect(() => {
    if (!campaignId) return;
    setData(null);
    newsletterApi.preview(campaignId, audience).then(setData).catch(() => setData({ html: '<p>Preview failed</p>' }));
  }, [campaignId, audience]);
  return (
    <Dialog open={!!campaignId} onOpenChange={v => !v && onClose()}>
      <DialogContent className="bg-[#0a0a0a] border-[#2a2a2a] text-white max-w-4xl" data-testid="nl-preview-dialog">
        <DialogHeader>
          <DialogTitle>Preview — {AUD_LABEL[audience]}</DialogTitle>
          <DialogDescription className="text-gray-300">Subject: {data?.subject || '(no subject yet)'}</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          {[['phone', Smartphone, 'Phone'], ['desktop', Monitor, 'Computer']].map(([k, Icon, l]) => (
            <button key={k} onClick={() => setMode(k)} data-testid={`nl-preview-${k}`}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1 border ${mode === k ? 'bg-green-500 text-black border-green-500' : 'border-[#2a2a2a] text-gray-200'}`}><Icon size={12} /> {l}</button>
          ))}
        </div>
        <div className="bg-[#1a1a1a] rounded-lg p-3 flex justify-center overflow-auto max-h-[65vh]">
          {data ? <iframe title="preview" srcDoc={data.html} data-testid="nl-preview-frame"
            style={{ width: mode === 'phone' ? 375 : 720, height: '62vh', border: 0, background: '#fff', borderRadius: 8 }} />
            : <Loader2 className="animate-spin text-gray-400" />}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function SendDialog({ campaignId, audience, onClose, onSent }) {
  const [info, setInfo] = useState(null);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!campaignId) return;
    setInfo(null); setTyped('');
    newsletterApi.eligible(campaignId).then(setInfo).catch(() => setInfo({ error: true }));
  }, [campaignId]);
  const count = info?.[audience] ?? 0;
  const days = info?.dailyLimit ? Math.ceil(Math.max(0, count + (info.alreadyQueued || 0) - (info.remainingToday || 0)) / info.dailyLimit) : 0;
  const go = async () => {
    setBusy(true);
    try {
      const res = await newsletterApi.send(campaignId, audience, typed);
      toast({ title: 'Newsletter is sending', description: `${res.queued} emails queued for ${AUD_LABEL[audience]}.` });
      onSent();
      onClose();
    } catch (err) {
      toast({ title: 'Send failed', description: err?.response?.data?.detail || 'Please try again.' });
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!campaignId} onOpenChange={v => !v && onClose()}>
      <DialogContent className="bg-[#0a0a0a] border-[#2a2a2a] text-white" data-testid="nl-send-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><AlertTriangle size={18} className="text-yellow-400" /> Final check before sending</DialogTitle>
          <DialogDescription className="text-gray-300">This sends real emails to {AUD_LABEL[audience]}. It can only be sent once to this audience.</DialogDescription>
        </DialogHeader>
        {!info ? <Loader2 className="animate-spin text-gray-400" /> : (
          <div className="space-y-3 text-sm">
            <div className="pbb-card p-4">
              <div className="text-3xl font-bold text-white" data-testid="nl-send-count">{count}</div>
              <div className="text-gray-300">eligible {AUD_LABEL[audience]} (unsubscribed and duplicate addresses removed)</div>
            </div>
            <div className="text-gray-300" data-testid="nl-send-limit">
              Daily newsletter limit: <b className="text-white">{info.dailyLimit}</b>. Left today: <b className="text-white">{info.remainingToday}</b>.
              {count + (info.alreadyQueued || 0) > info.remainingToday && <> The rest go out automatically at 9am ET each day — about <b className="text-white">{days}</b> more day(s).</>}
            </div>
            <label className="block text-gray-200">Type <b>SEND</b> to confirm
              <input value={typed} onChange={e => setTyped(e.target.value.toUpperCase())} className="pbb-input mt-1" data-testid="nl-send-confirm-input" />
            </label>
            <button onClick={go} disabled={busy || typed !== 'SEND' || !count} data-testid="nl-send-confirm"
              className="w-full px-4 py-2.5 rounded-lg bg-green-600 hover:bg-green-700 font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50">
              {busy ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />} Send to {count} {AUD_LABEL[audience]}
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
